import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import ConfigCard from './ConfigCard';
import { lookupCoordinates } from '../../config/geocode';

/**
 * "Contact information" card. Backed by the storefront's `SF_INFO_*`
 * settings — these populate the footer and the order-confirmation emails.
 *
 * value shape:
 *   { shopName, addressLine, city, vatNumber, phone, email, website, geoLat, geoLng }
 *
 * Field ↔ legacy key mapping (resolved by the WebshopConfigScreen save flow):
 *   shopName    ↔ SF_INFO_NAME
 *   titleSuffix ↔ SF_PAGE_TITLE_SUFFIX (after every page title in the browser tab)
 *   addressLine ↔ SF_INFO_ADDRESS
 *   city        ↔ SF_INFO_CITY    (also typically holds postcode + country)
 *   vatNumber   ↔ SF_INFO_VAT
 *   phone       ↔ SF_INFO_PHONE
 *   email       ↔ SF_INFO_EMAIL
 *   website     ↔ SF_INFO_WEBSITE
 *   geoLat/Lng  ↔ SF_INFO_ADDRESS_GEO ("lat,lng"): with both filled in, the
 *                 footer address becomes a Google Maps link.
 */
const ContactInformationCard = ({ value = {}, onChange, theme, t, geocode, gmapsApiKey = '' }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb = '') => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (e) => onChange && onChange(k, e.target.value);

    // Coordinates from the address above (config/geocode: through the shop,
    // the browser as fallback) — the legacy admin only had the two fields.
    const [geoBusy, setGeoBusy] = useState(false);
    const [geoError, setGeoError] = useState('');
    const lookup = async () => {
        const address = [get('addressLine'), get('city')].filter(Boolean).join(', ');
        if (!address.trim()) { setGeoError(tr('cfg_about_geo_no_address', 'Enter an address first.')); return; }
        setGeoBusy(true);
        setGeoError('');
        try {
            const r = await lookupCoordinates(address, { viaShop: geocode, browserKey: gmapsApiKey });
            if (r.lat !== undefined) {
                onChange && onChange('geoLat', r.lat);
                onChange && onChange('geoLng', r.lng);
            } else if (r.error === 'no_key')    setGeoError(tr('cfg_about_geo_no_key', 'No Google Maps API key set (see Connections).'));
            else if (r.error === 'not_found')   setGeoError(tr('cfg_about_geo_not_found', 'No coordinates found for this address.'));
            else setGeoError(r.error || tr('cfg_about_geo_error', 'Lookup failed.'));
        } catch {
            setGeoError(tr('cfg_about_geo_error', 'Lookup failed. Check the API key or network.'));
        } finally {
            setGeoBusy(false);
        }
    };

    return (
        <ConfigCard
            anchor="cfg-card-footer-contact"
            title={tr('cfg_footer_contact_title', 'Contact information')}
            description={tr('cfg_footer_contact_desc', 'Shown at the bottom of every page.')}
            theme={theme}
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField size="small" label={tr('cfg_footer_contact_shop_name', 'Shop name')} value={get('shopName')}    onChange={set('shopName')} />
                <TextField size="small" label={tr('cfg_footer_contact_title_suffix', 'Browser tab title')} value={get('titleSuffix')} onChange={set('titleSuffix')}
                    helperText={tr('cfg_footer_contact_title_suffix_help', 'Shown after the page title, e.g. "Dresses | <this text>"')} />
                <TextField size="small" label={tr('cfg_footer_contact_address',   'Address')}   value={get('addressLine')} onChange={set('addressLine')} sx={{ gridColumn: { sm: '1 / -1' } }} />
                <TextField size="small" label={tr('cfg_footer_contact_city',      'City')}      value={get('city')}        onChange={set('city')} />
                <TextField size="small" label={tr('cfg_footer_contact_vat',       'VAT number')}value={get('vatNumber')}   onChange={set('vatNumber')} />
                <TextField size="small" label={tr('cfg_footer_contact_phone',     'Phone')}     value={get('phone')}       onChange={set('phone')} />
                <TextField size="small" label={tr('cfg_footer_contact_email',     'Email')}     type="email" value={get('email')} onChange={set('email')} />
                <TextField size="small" label={tr('cfg_footer_contact_website',   'Website')}   value={get('website')}     onChange={set('website')} sx={{ gridColumn: { sm: '1 / -1' } }} />
                <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                        {tr('cfg_footer_contact_geo_help', 'With both coordinates filled in, the address in the footer opens Google Maps.')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField size="small" label={tr('cfg_footer_contact_geo_lat', 'Latitude')}  value={get('geoLat')} onChange={set('geoLat')} sx={{ flex: '1 1 140px' }} inputProps={{ 'data-geo': 'lat' }} />
                        <TextField size="small" label={tr('cfg_footer_contact_geo_lng', 'Longitude')} value={get('geoLng')} onChange={set('geoLng')} sx={{ flex: '1 1 140px' }} inputProps={{ 'data-geo': 'lng' }} />
                        <Button size="small" variant="outlined" onClick={lookup} disabled={geoBusy}
                            startIcon={geoBusy ? <CircularProgress size={14} color="inherit" /> : <PlaceIcon fontSize="small" />}
                            sx={{ textTransform: 'none' }}>
                            {tr('cfg_footer_contact_geo_lookup', 'Look up from the address')}
                        </Button>
                    </Box>
                    {geoError && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mt: 0.75 }}>{geoError}</Typography>
                    )}
                </Box>
            </Box>
        </ConfigCard>
    );
};

ContactInformationCard.propTypes = {
    value:       PropTypes.object,
    onChange:    PropTypes.func,
    theme:       PropTypes.string,
    t:           PropTypes.func,
    geocode:     PropTypes.func,
    gmapsApiKey: PropTypes.string,
};

export default ContactInformationCard;
